import { useQuery } from '@tanstack/react-query'
import {
  analyticsFetch,
  apiFetch,
  getCallerAgentId,
  hasLiveAnalytics,
  hasLiveApi,
} from '@/api/client'
import { isUnreachableApiError } from '@/api/unreachable'
import type { AgentNode } from '@/api/map/agent'
import {
  buildMemberDetail,
  mergeAnalyticsMemberDetail,
  overlayNode,
  type AnalyticsMemberDetail,
} from '@/api/map/member'
import * as api from '@/data/api'
import { keys } from '@/api/hooks/shared/keys'
import type { DateRange } from '@/data/types'

/**
 * Live member detail = hierarchy money + analytics period/trend (Lab Overview).
 * Hierarchy alone when analytics is off/unreachable — never invent chart numbers.
 */
async function loadMemberDetail(id: string, range: DateRange) {
  if (!hasLiveApi()) return api.getMemberDetail(id, range)
  try {
    const rootId = getCallerAgentId()!
    const [tree, node] = await Promise.all([
      apiFetch<AgentNode[]>(`/v1/agents/${rootId}/tree`),
      apiFetch<AgentNode>(`/v1/agents/${id}`),
    ])
    const base = buildMemberDetail(overlayNode(tree, node), id, rootId)
    if (!base) return null
    if (!hasLiveAnalytics()) return base
    try {
      const from = range.from.slice(0, 10)
      const to = range.to.slice(0, 10)
      const qs = new URLSearchParams({ from, to }).toString()
      const live = await analyticsFetch<AnalyticsMemberDetail>(
        `/v1/members/${id}/detail?${qs}`,
      )
      return mergeAnalyticsMemberDetail(base, live)
    } catch (err) {
      if (isUnreachableApiError(err)) return base
      // Soft-fail: keep hierarchy KPIs rather than blank the whole member page.
      return base
    }
  } catch (err) {
    if (isUnreachableApiError(err)) return null
    throw err
  }
}

export function useMemberDetail(id: string | undefined, range: DateRange) {
  return useQuery({
    queryKey: keys.memberDetail(id ?? '', range),
    queryFn: () => loadMemberDetail(id!, range),
    enabled: Boolean(id),
  })
}
